import * as Ably from 'ably';
import { LiveObjects } from 'ably/liveobjects';

import { fetchAblyTokenRequest } from '@lib/realtime/ably-token';

/**
 * Singleton Ably client shared across every channel subscription on the page.
 * One WebSocket, N channels: a squad can span ten fixtures, and with the
 * channel split each fixture carries a main channel plus one channel per
 * subscribed player, so a client per channel is not an option.
 *
 * The LiveObjects plugin is what makes `channel.object` available. Without it
 * an attach succeeds and the root map is simply never populated, so the pitch
 * would sit at zero points with nothing in the console to explain it.
 *
 * Lifecycle:
 *  - `acquireRealtimeClient(matchId, getAccessToken)` returns the singleton,
 *    creating it on first call.
 *  - `releaseRealtimeClient(matchId)` decrements the refcount; the singleton
 *    closes when no matches remain subscribed.
 *
 * Individual channels are attached and detached by their own subscriptions
 * (see `channel-root.ts`); this module owns only the connection.
 *
 * Tokens come from the public API, which verifies the caller before granting
 * anything (FFH-2225). See `lib/realtime/ably-token.ts`.
 */

export type GetAccessToken = () => Promise<string>;

let client: Ably.Realtime | null = null;
const subscriptionCounts = new Map<string, number>();
let cachedAccessTokenGetter: GetAccessToken | null = null;

const subscribedMatchIds = (): string[] =>
  Array.from(subscriptionCounts.keys());

const hasNoActiveSubscriptions = (): boolean => subscriptionCounts.size === 0;

const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * Resolve once the client connection is in `connected`. Rejects if it
 * lands in `failed` first.
 */
export const waitForConnected = (target: Ably.Realtime): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (target.connection.state === 'connected') {
      resolve();
      return;
    }
    const onConnected = () => {
      target.connection.off('failed', onFailed);
      resolve();
    };
    const onFailed = (stateChange: Ably.ConnectionStateChange) => {
      target.connection.off('connected', onConnected);
      reject(
        new Error(
          `Ably connection failed: ${stateChange.reason?.message ?? 'unknown'}`
        )
      );
    };
    target.connection.once('connected', onConnected);
    target.connection.once('failed', onFailed);
  });

const buildAuthCallback = (): Ably.AuthOptions['authCallback'] => {
  return async (_tokenParams, callback) => {
    try {
      if (!cachedAccessTokenGetter) {
        callback(
          'Realtime client not initialised with access-token getter',
          null
        );
        return;
      }
      const matchIds = subscribedMatchIds();
      const accessToken = await cachedAccessTokenGetter();
      const tokenRequest = await fetchAblyTokenRequest(accessToken, matchIds);
      callback(null, tokenRequest);
    } catch (error) {
      console.error('[ably] auth callback failed', error);
      callback(toErrorMessage(error), null);
    }
  };
};

const createClient = (): Ably.Realtime =>
  new Ably.Realtime({
    plugins: { LiveObjects },
    authCallback: buildAuthCallback(),
    autoConnect: false
  });

export const acquireRealtimeClient = async (
  matchId: string,
  getAccessToken: GetAccessToken
): Promise<Ably.Realtime> => {
  cachedAccessTokenGetter = getAccessToken;

  const previousCount = subscriptionCounts.get(matchId) ?? 0;
  subscriptionCounts.set(matchId, previousCount + 1);

  if (!client) {
    client = createClient();
  }

  // No re-authorise when a new match joins an existing client. Tokens from the
  // public API grant the whole `match:` namespace, so a token minted for one
  // match already authorises every other; re-minting would buy nothing and cost
  // a round trip before the channel could attach.
  //
  // The live match engine narrowed capability to the matchIds it was given,
  // which is what a re-authorise here used to satisfy: without it, attaching a
  // match the token did not name failed with "Channel denied access based on
  // given capability". Dropping it also keeps a gameweek's worth of fixtures
  // mounting at once well inside the endpoint's per-user mint budget.
  //
  // This holds only while the grant stays namespace-wide. Narrowing it to a
  // caller's entitled matches, which the endpoint is expected to do eventually,
  // means restoring a re-authorise here, and the symptom of forgetting is a
  // match that silently never attaches.
  return client;
};

export const releaseRealtimeClient = (matchId: string): void => {
  const current = subscriptionCounts.get(matchId) ?? 0;

  if (current <= 1) {
    subscriptionCounts.delete(matchId);
  } else {
    subscriptionCounts.set(matchId, current - 1);
  }

  if (hasNoActiveSubscriptions() && client) {
    client.close();
    client = null;
    cachedAccessTokenGetter = null;
  }
};

// Test-only helper. Not exported via a barrel; consumers should not use it.
export const __resetRealtimeClientForTests = (): void => {
  if (client) {
    client.close();
  }
  client = null;
  subscriptionCounts.clear();
  cachedAccessTokenGetter = null;
};
